/**
 * D245: every `use` arm can be reached.
 *
 * @remarks
 * An item's `useInteractions` are read first match wins among the arms aimed
 * where the player aimed — at one thing, at an exit, or (D245) at nothing,
 * the thing used on its own. An arm with no conditions always matches, so an
 * arm after it aimed the same way can never run: its lines sit in the JSON
 * and nobody hears them. The thing used on its own made the mistake easy to
 * make — a refusal written first, the real answer under it — so it is
 * refused for every aim, and no shipped pack had a pair arm that tripped it.
 *
 * @packageDocumentation
 */

import { type ContentPackDefinition, shadowedArmsOf, type UseInteraction } from '@orpheus/content-schema';

import { brokenReference } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';

/**
 * Where a `use` arm is aimed — the item, the exit, or (D245) nothing.
 *
 * @param interaction - The arm
 * @returns Its aim, as the failure names it
 */
const aimOf = (interaction: UseInteraction): string =>
	interaction.targetItemId === undefined
		? interaction.targetExitId === undefined
			? 'the thing used on its own'
			: `the exit ${interaction.targetExitId}`
		: `the item ${interaction.targetItemId}`;

/**
 * Refuse every `use` arm an earlier unconditioned arm aimed the same way
 * shadows — first match wins, the rule D246's branch shares
 * (`shadowedArmsOf`).
 *
 * @param contentPack - The parsed pack
 * @returns One failure per arm that could never run
 */
export const validateUseArmsReachable = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] =>
	contentPack.items.flatMap((item, itemIndex) =>
		shadowedArmsOf(item.useInteractions, (interaction) => interaction.conditions.length === 0, aimOf).map(({ aim, armIndex }) =>
			brokenReference(
				`items[${String(itemIndex)}].useInteractions[${String(armIndex)}]`,
				item.itemId,
				`an earlier arm with no conditions already answers ${aim} — arms are read first match wins, so this one could never run`,
			),
		),
	);
